import { useState } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useApolloClient } from '@apollo/client/react';
import { useDataDomain, useDataObjectsByDomain } from '../../graphql/queries';
import {
  DetailSkeleton,
  EmptyState,
  StatusBadge,
  ErrorBoundary,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { DataObjectForm } from '../../components/information';
import {
  deleteDataDomain,
  createDataObject,
  updateDataObject,
  deleteDataObject,
  ApiError,
  type CreateDataObjectInput,
  type UpdateDataObjectInput,
} from '../../lib/apiClient';
import type { DataObject } from '../../graphql/types';
import type { CreateDataObjectFormData } from '../../components/forms/validation';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import '@tylertech/forge/banner';
import { safeExternalHref } from '../../lib/safeExternalHref';
import './$dataDomainId.css';

export const Route = createFileRoute('/information/$dataDomainId')({
  component: DataDomainDetailPage,
});

export { DataDomainDetailPage };

type DataObjectFormMode = { mode: 'create' } | { mode: 'edit'; dataObject: DataObject };

function DataDomainDetailPage(): JSX.Element {
  const { dataDomainId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataObjectFormState, setDataObjectFormState] = useState<DataObjectFormMode | null>(null);
  const [isSubmittingDataObject, setIsSubmittingDataObject] = useState(false);
  const [deleteObjectDialogTarget, setDeleteObjectDialogTarget] = useState<DataObject | null>(null);
  const [isDeletingObject, setIsDeletingObject] = useState(false);

  // If on a child route (edit), render only the outlet — otherwise the
  // /information/$dataDomainId/edit child route never appears; this
  // component (its parent) is always rendered instead (issue #599).
  const isChildRoute = location.pathname.endsWith('/edit');

  const { data, loading, error: fetchError } = useDataDomain(dataDomainId);
  const { data: objectsData, refetch: refetchObjects } = useDataObjectsByDomain(dataDomainId);

  const domain = data?.dataDomain;
  const dataObjects = objectsData?.dataObjectsByDomain?.edges?.map((e) => e.node) || [];

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  if (loading) {
    return (
      <div className="data-domain-detail-page">
        <DetailSkeleton cards={3} />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="data-domain-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load data domain"
          description={fetchError.message}
          actionLabel="Back to Data Domains"
          /* c8 ignore next */
          onAction={() => void navigate({ to: '/information', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="data-domain-detail-page">
        <EmptyState
          icon="search_off"
          title="Data domain not found"
          description={`No data domain found with ID: ${dataDomainId}`}
          actionLabel="Back to Data Domains"
          /* c8 ignore next */
          onAction={() => void navigate({ to: '/information', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const formatDate = (dateString: string | null | undefined): string => {
    /* c8 ignore next */
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleEdit = (): void => {
    void navigate({
      to: '/information/$dataDomainId/edit',
      params: { dataDomainId: domain.entityId },
    });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteDataDomain(dataDomainId, false);
      await apolloClient.refetchQueries({ include: ['ListDataDomains'] });
      void navigate({ to: '/information', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDataObjectSubmit = async (formData: CreateDataObjectFormData): Promise<void> => {
    setIsSubmittingDataObject(true);
    setError(null);
    try {
      if (dataObjectFormState?.mode === 'edit') {
        const input: UpdateDataObjectInput = {
          name: formData.name,
          /* c8 ignore next */
          description: formData.description || undefined,
          objectType: formData.objectType,
          classification: formData.classification,
          /* c8 ignore next */
          containsPII: formData.containsPII ?? false,
          /* c8 ignore next */
          containsPCI: formData.containsPCI ?? false,
          /* c8 ignore next */
          containsPHI: formData.containsPHI ?? false,
          /* c8 ignore next */
          encryptionRequired: formData.encryptionRequired ?? false,
          /* c8 ignore next */
          maskingRequired: formData.maskingRequired ?? false,
          /* c8 ignore next */
          systemOfRecord: formData.systemOfRecord || undefined,
          /* c8 ignore next */
          isGoldenRecord: formData.isGoldenRecord ?? false,
          lifecycleStatus: formData.lifecycleStatus,
          /* c8 ignore next */
          notes: formData.notes || undefined,
        };
        await updateDataObject(dataObjectFormState.dataObject.entityId, input);
      } else {
        const input: CreateDataObjectInput = {
          name: formData.name,
          /* c8 ignore next */
          description: formData.description || undefined,
          domainId: dataDomainId,
          objectType: formData.objectType,
          classification: formData.classification,
          /* c8 ignore next */
          containsPII: formData.containsPII ?? false,
          /* c8 ignore next */
          containsPCI: formData.containsPCI ?? false,
          /* c8 ignore next */
          containsPHI: formData.containsPHI ?? false,
          /* c8 ignore next */
          encryptionRequired: formData.encryptionRequired ?? false,
          /* c8 ignore next */
          maskingRequired: formData.maskingRequired ?? false,
          /* c8 ignore next */
          systemOfRecord: formData.systemOfRecord || undefined,
          /* c8 ignore next */
          isGoldenRecord: formData.isGoldenRecord ?? false,
          lifecycleStatus: formData.lifecycleStatus,
          /* c8 ignore next */
          notes: formData.notes || undefined,
        };
        await createDataObject(input);
      }
      setDataObjectFormState(null);
      void refetchObjects();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmittingDataObject(false);
    }
  };

  const handleConfirmDeleteObject = async (): Promise<void> => {
    if (!deleteObjectDialogTarget) return;
    setIsDeletingObject(true);
    setError(null);
    try {
      await deleteDataObject(deleteObjectDialogTarget.entityId, false);
      setDeleteObjectDialogTarget(null);
      void refetchObjects();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsDeletingObject(false);
    }
  };

  return (
    <ErrorBoundary>
      <div className="data-domain-detail-page">
        <div className="breadcrumb">
          <Link to="/information" search={{ page: 1, pageSize: 25 }} className="breadcrumb-link">
            <IconElement name="arrow_back" aria-hidden="true" />
            <span>Back to Data Domains</span>
          </Link>
        </div>

        {error && (
          <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
            <div className="banner-content">
              <IconElement name="error" aria-hidden="true" />
              <span>{error}</span>
            </div>
          </BannerElement>
        )}

        <div className="data-domain-header">
          <div className="data-domain-header-content">
            <h1 className="forge-typography--headline4">{domain.name}</h1>
            {domain.description && (
              <p className="forge-typography--body2 data-domain-description">
                {domain.description}
              </p>
            )}
          </div>
          <div className="data-domain-actions">
            <PermissionGate permission="information:update">
              <ForgeButton type="button" variant="outlined" onClick={handleEdit}>
                Edit
              </ForgeButton>
            </PermissionGate>
            <PermissionGate permission="information:delete">
              <ForgeButton
                type="button"
                variant="outlined"
                className="forge-button--danger"
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </ForgeButton>
            </PermissionGate>
          </div>
        </div>

        <div className="data-domain-content">
          <CardElement className="data-domain-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Overview</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Domain Type</span>
                <span className="detail-value detail-value--capitalize">{domain.domainType}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Lifecycle Status</span>
                <span className="detail-value detail-value--capitalize">
                  {domain.lifecycleStatus}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Criticality</span>
                <span className="detail-value detail-value--capitalize">
                  {domain.criticalityLevel}
                </span>
              </div>
              {domain.qualityScore != null && (
                <div className="detail-row">
                  <span className="detail-label">Quality Score</span>
                  <span className="detail-value">{domain.qualityScore}/100</span>
                </div>
              )}
              {domain.notes && (
                <div className="detail-row">
                  <span className="detail-label">Notes</span>
                  <span className="detail-value">{domain.notes}</span>
                </div>
              )}
            </div>
          </CardElement>

          <CardElement className="data-domain-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Classification &amp; Compliance</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Classification</span>
                <span className="detail-value">
                  <StatusBadge variant="classification" value={domain.defaultClassification} />
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contains PII</span>
                <span className="detail-value">{domain.containsPII ? 'Yes' : 'No'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contains PCI</span>
                <span className="detail-value">{domain.containsPCI ? 'Yes' : 'No'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contains PHI</span>
                <span className="detail-value">{domain.containsPHI ? 'Yes' : 'No'}</span>
              </div>
              {domain.retentionPeriodDays && (
                <div className="detail-row">
                  <span className="detail-label">Retention Period</span>
                  <span className="detail-value">{domain.retentionPeriodDays} days</span>
                </div>
              )}
              {domain.complianceFrameworks && domain.complianceFrameworks.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Compliance</span>
                  <span className="detail-value">{domain.complianceFrameworks.join(', ')}</span>
                </div>
              )}
            </div>
          </CardElement>

          <CardElement className="data-domain-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Governance</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Data Steward</span>
                <span className="detail-value detail-value-code">{domain.dataStewardId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Business Unit</span>
                <span className="detail-value detail-value-code">
                  {domain.owningBusinessUnitId}
                </span>
              </div>
              {domain.technicalOwnerId && (
                <div className="detail-row">
                  <span className="detail-label">Technical Owner</span>
                  <span className="detail-value detail-value-code">{domain.technicalOwnerId}</span>
                </div>
              )}
              {domain.documentationUrl && (
                <div className="detail-row">
                  <span className="detail-label">Documentation</span>
                  <span className="detail-value">
                    {safeExternalHref(domain.documentationUrl) ? (
                      <a
                        href={safeExternalHref(domain.documentationUrl) ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="data-domain-docs-link"
                      >
                        View Docs
                      </a>
                    ) : (
                      domain.documentationUrl
                    )}
                  </span>
                </div>
              )}
            </div>
          </CardElement>

          <CardElement className="data-domain-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Metadata</h2>
            </div>
            <div className="card-content">
              <div className="detail-row">
                <span className="detail-label">Entity ID</span>
                <span className="detail-value detail-value-code">{domain.entityId}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(domain._createdAt)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created By</span>
                <span className="detail-value">{domain._createdBy}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">{formatDate(domain._updatedAt)}</span>
              </div>
            </div>
          </CardElement>
        </div>

        <div className="data-objects-section">
          <div className="data-objects-section-header">
            <h2 className="forge-typography--headline5">Data Objects</h2>
            <PermissionGate permission="information:create">
              <ForgeButton
                type="button"
                variant="raised"
                onClick={() => setDataObjectFormState({ mode: 'create' })}
              >
                Add Data Object
              </ForgeButton>
            </PermissionGate>
          </div>

          {dataObjects.length === 0 ? (
            <div className="data-object-empty">No data objects in this domain yet.</div>
          ) : (
            dataObjects.map((obj) => (
              <div
                key={obj.entityId}
                className="data-object-row"
                role="button"
                tabIndex={0}
                onClick={() => setDataObjectFormState({ mode: 'edit', dataObject: obj })}
                /* c8 ignore next 5 */
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setDataObjectFormState({ mode: 'edit', dataObject: obj });
                  }
                }}
              >
                <div className="data-object-row-info">
                  <span className="data-object-row-name">{obj.name}</span>
                  <span className="data-object-row-meta">
                    {obj.objectType} · {obj.lifecycleStatus}
                  </span>
                </div>
                <div className="data-object-row-badges">
                  <StatusBadge variant="classification" value={obj.classification} />
                  <PermissionGate permission="information:delete">
                    <ForgeButton
                      type="button"
                      variant="icon-button"
                      aria-label={`Delete ${obj.name}`}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setDeleteObjectDialogTarget(obj);
                      }}
                    >
                      <IconElement name="delete" aria-hidden="true" />
                    </ForgeButton>
                  </PermissionGate>
                </div>
              </div>
            ))
          )}
        </div>

        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          title="Delete Data Domain"
          message="Are you sure you want to delete this data domain?"
          itemName={domain.name}
          warning="This action will soft delete the data domain and all associated data objects. It can be recovered by an administrator."
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setDeleteDialogOpen(false)}
          isDeleting={isDeleting}
        />

        <DeleteConfirmationDialog
          open={deleteObjectDialogTarget !== null}
          title="Delete Data Object"
          message="Are you sure you want to delete this data object?"
          itemName={deleteObjectDialogTarget?.name ?? ''}
          warning="This action will soft delete the data object."
          onConfirm={() => void handleConfirmDeleteObject()}
          onCancel={() => setDeleteObjectDialogTarget(null)}
          isDeleting={isDeletingObject}
        />

        {dataObjectFormState && (
          <div className="data-object-form-overlay">
            <div className="data-object-form-panel">
              <h3 className="forge-typography--headline6">
                {dataObjectFormState.mode === 'create' ? 'Add Data Object' : 'Edit Data Object'}
              </h3>
              <DataObjectForm
                domainId={dataDomainId}
                mode={dataObjectFormState.mode}
                defaultValues={
                  dataObjectFormState.mode === 'edit'
                    ? {
                        name: dataObjectFormState.dataObject.name,
                        description: dataObjectFormState.dataObject.description ?? undefined,
                        objectType: dataObjectFormState.dataObject.objectType,
                        classification: dataObjectFormState.dataObject.classification,
                        containsPII: dataObjectFormState.dataObject.containsPII,
                        containsPCI: dataObjectFormState.dataObject.containsPCI,
                        containsPHI: dataObjectFormState.dataObject.containsPHI,
                        encryptionRequired: dataObjectFormState.dataObject.encryptionRequired,
                        maskingRequired: dataObjectFormState.dataObject.maskingRequired,
                        systemOfRecord: dataObjectFormState.dataObject.systemOfRecord ?? undefined,
                        isGoldenRecord: dataObjectFormState.dataObject.isGoldenRecord,
                        lifecycleStatus: dataObjectFormState.dataObject.lifecycleStatus,
                        notes: dataObjectFormState.dataObject.notes ?? undefined,
                      }
                    : undefined
                }
                onSubmit={handleDataObjectSubmit}
                onCancel={() => setDataObjectFormState(null)}
                isSubmitting={isSubmittingDataObject}
              />
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
